import React from 'react';
import { 
  FaBook,
  FaChild,
  FaGraduationCap,
  FaSchool,
  FaArrowRight
} from 'react-icons/fa';

const AcademicPrograms = () => {
  const programs = [
    {
      id: 1,
      age: "3-5 Years",
      title: "Pre-Primary",
      subtitle: "LKG & UKG",
      description: "Montessori-based learning with sensory activities and play",
      icon: FaChild,
      iconGradient: "from-pink-400 to-rose-500",
      bgGradient: "from-pink-50 via-rose-50 to-pink-100",
      borderColor: "border-pink-200",
      tags: ["Playway", "Phonics", "Art", "Music"],
      features: [
        { label: "Play-based", color: "from-pink-400 to-rose-500" },
        { label: "Sensory", color: "from-pink-400 to-rose-500" },
        { label: "Social Skills", color: "from-pink-400 to-rose-500" }
      ]
    },
    {
      id: 2,
      age: "6-10 Years",
      title: "Primary",
      subtitle: "Grade I - V",
      description: "Foundation building with activity-based learning approach",
      icon: FaSchool,
      iconGradient: "from-blue-400 to-cyan-500",
      bgGradient: "from-blue-50 via-cyan-50 to-blue-100",
      borderColor: "border-blue-200",
      tags: ["Science", "Maths", "SST", "Computer", "Language"],
      features: [
        { label: "Core Subjects", color: "from-blue-400 to-cyan-500" },
        { label: "Values", color: "from-blue-400 to-cyan-500" },
        { label: "Creativity", color: "from-blue-400 to-cyan-500" }
      ]
    },
    {
      id: 3,
      age: "11-13 Years",
      title: "Middle School",
      subtitle: "Grade VI - VIII",
      description: "Subject specialization with project-based learning",
      icon: FaBook,
      iconGradient: "from-emerald-400 to-teal-500",
      bgGradient: "from-emerald-50 via-teal-50 to-emerald-100",
      borderColor: "border-emerald-200",
      tags: ["Science", "Maths", "SST", "IT", "Languages"],
      features: [
        { label: "Specialization", color: "from-emerald-400 to-teal-500" },
        { label: "Projects", color: "from-emerald-400 to-teal-500" },
        { label: "Labs", color: "from-emerald-400 to-teal-500" }
      ]
    },
    {
      id: 4,
      age: "14-16 Years",
      title: "Secondary",
      subtitle: "Grade IX - X",
      description: "Board preparation with career guidance",
      icon: FaGraduationCap,
      iconGradient: "from-amber-400 to-orange-500",
      bgGradient: "from-amber-50 via-orange-50 to-amber-100",
      borderColor: "border-amber-200",
      tags: ["All Streams", "Lab Work", "Projects"],
      features: [
        { label: "Board Prep", color: "from-amber-400 to-orange-500" },
        { label: "Career", color: "from-amber-400 to-orange-500" },
        { label: "Exams", color: "from-amber-400 to-orange-500" }
      ]
    }
  ];

  return (
    <section className="py-12 bg-gradient-to-b from-gray-50 to-white">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto mb-10 text-center">
          <div className="inline-flex items-center px-3 py-1 mb-3 text-xs font-medium text-purple-600 rounded-full bg-gradient-to-r from-purple-50 to-violet-100">
            <FaBook className="w-3 h-3 mr-1.5" />
            Academic Programs
          </div>
          <h2 className="mb-2 text-xl font-bold text-gray-900">Structured Learning Journey</h2>
          <p className="text-xs text-gray-600">Age-appropriate curriculum designed for holistic development</p>
        </div>

        {/* Programs Grid */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {programs.map((program) => (
            <div
              key={program.id}
              className={`group relative p-5 rounded-2xl bg-gradient-to-br ${program.bgGradient} border ${program.borderColor} transition-all duration-300 hover:shadow-lg hover:-translate-y-1`}
            >
              {/* Age Badge */}
              <div className="absolute top-3 right-3">
                <span className={`px-2 py-1 text-xs font-medium text-gray-700 bg-white/80 backdrop-blur-sm rounded-full border ${program.borderColor} shadow-sm`}>
                  {program.age}
                </span>
              </div>

              {/* Icon */}
              <div className="mb-4">
                <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br ${program.iconGradient} shadow-sm`}>
                  <div className="text-white">
                    <program.icon className="w-6 h-6" />
                  </div>
                </div>
              </div>

              {/* Title */}
              <h3 className="mb-1 text-base font-bold text-gray-900">
                {program.title}
                <span className="block text-xs font-normal text-gray-500">{program.subtitle}</span>
              </h3>

              {/* Description */}
              <p className="mb-4 text-xs leading-relaxed text-gray-600">{program.description}</p>

              {/* Tags */}
              <div className="mb-4">
                <div className="flex flex-wrap gap-1.5">
                  {program.tags.map((tag, index) => (
                    <span
                      key={index}
                      className={`px-2 py-1 text-xs font-medium text-gray-700 bg-white/80 backdrop-blur-sm rounded-full border ${program.borderColor}`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Features */}
              <div className="pt-3 border-t border-gray-200/50">
                <div className="flex items-center justify-between">
                  {program.features.map((feature, index) => (
                    <div key={index} className="flex flex-col items-center">
                      <div className={`w-2 h-2 rounded-full bg-gradient-to-br ${feature.color} mb-1`}></div>
                      <span className="text-xs text-gray-600">{feature.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Hover Arrow */}
              <div className="absolute transition-opacity duration-300 opacity-0 bottom-3 right-3 group-hover:opacity-100">
                <div className={`p-1.5 rounded-full bg-gradient-to-br ${program.iconGradient} shadow-sm`}>
                  <FaArrowRight className="w-2.5 h-2.5 text-white" />
                </div>
              </div>

              {/* Decorative Element */}
              <div className={`absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl ${program.iconGradient} rounded-bl-2xl opacity-5`}></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AcademicPrograms;